import type {
  CashManagementTransactionType,
  CashTransactionGroupType,
  TransactionType,
  TransactionSourceType,
  TransactionInitiatedBy,
} from '@pflegenavi/shared/api';
import type {
  CashListTransactionGroupSingle,
  TransformedCashTransactionGroup,
} from './api';

/**
 * Transforms a Phoenix cash transaction group response to match the legacy format
 * @param data The raw Phoenix API response
 * @returns Transformed cash transaction group matching legacy format
 */
export function transformPhoenixCashTransactionGroup(data: {
  cash_list_transaction_group: CashListTransactionGroupSingle;
}): TransformedCashTransactionGroup {
  const group = data.cash_list_transaction_group;
  const groupList = group.cash_transaction_group_list_entry;
  const pliantTransactionCreatedAt = group.cash_list_transactions?.find(
    (t) => t.pliant_transaction?.created_at
  )?.pliant_transaction?.created_at;

  return {
    id: group.id,
    type: group.group_type as CashTransactionGroupType,
    cashListId: group.cash_list_id,
    updateDate: groupList?.update_date
      ? new Date(groupList.update_date)
      : new Date(),
    cashChanged: groupList?.cash_changed || 0,
    separateFeeAmount: groupList?.separate_fee_amount || 0,
    immediateFeeAmount: groupList?.immediate_fee_amount || 0,
    amountLinked: groupList?.amount_linked || 0,
    separateFeeAmountLinked: groupList?.separate_fee_amount_linked || 0,
    immediateFeeAmountLinked: groupList?.immediate_fee_amount_linked || 0,
    linkCount: calculateLinkCount(groupList),
    receiptLinkCount: groupList?.receipt_count || 0,
    transactionLinkCount: groupList?.transaction_count || 0,
    payoutLinkCount:
      (groupList?.payout_count || 0) +
      (groupList?.nursing_home_payout_count || 0),
    paymentIntentLinkCount: groupList?.payment_intent_count || 0,
    cashListTransactionCount: groupList?.cash_list_transaction_count || 0,
    openReceiptBatchCount: groupList?.open_receipt_batch_count || 0,
    submittedReceiptBatchCount: groupList?.submitted_receipt_batch_count || 0,
    cashTransfers: group.cash_transfers || groupList?.transfer_count || 0,
    mismatchConfirmationDate: group.mismatch_confirmation_timestamp
      ? new Date(group.mismatch_confirmation_timestamp)
      : undefined,
    reversed: group.reversed || groupList?.reversed || false,
    manualTransactionCreationAllowed: group.manual_transaction_creation_allowed,
    pliantTransactionAmount: groupList?.pliant_transaction_amount,
    pliantTransactionCreatedAt: pliantTransactionCreatedAt
      ? new Date(pliantTransactionCreatedAt)
      : undefined,
    cashListTransactions: transformCashListTransactions(
      group.cash_list_transactions
    ),
    transactions: transformLinkedTransactions(
      group.cash_list_transaction_group_links
    ),
    expensePartCount: groupList?.expense_part_count ?? 0,
  };
}

/**
 * Calculates the total link count from the group list entry
 */
function calculateLinkCount(
  groupList?: CashListTransactionGroupSingle['cash_transaction_group_list_entry']
): number {
  if (!groupList) {
    return 0;
  }

  return (
    (groupList.receipt_count || 0) +
    (groupList.transaction_count || 0) +
    (groupList.payment_intent_count || 0) +
    (groupList.payout_count || 0) +
    (groupList.nursing_home_payout_count || 0) +
    (groupList.open_receipt_batch_count || 0) // Intentionally not counting submitted receipt batches, since for submitted receipt batches, receipts exist that are counted instead
  );
}

/**
 * Transforms cash list transactions to the legacy format
 */
function transformCashListTransactions(
  transactions?: CashListTransactionGroupSingle['cash_list_transactions']
): TransformedCashTransactionGroup['cashListTransactions'] {
  if (!transactions) {
    return undefined;
  }

  return transactions.map((t) => ({
    id: t.id,
    cashListId: t.cash_list_id,
    type: t.type as CashManagementTransactionType,
    amount: t.amount,
    separateFeeAmount: t.separate_fee_amount,
    separateFeePercentage: t.separate_fee_percentage,
    immediateFeeAmount: t.immediate_fee_amount,
    immediateFeeTracked: t.immediate_fee_tracked,
    date: new Date(t.inserted_at),
    eventDate: new Date(t.event_datetime),
    employee: t.employee
      ? {
          id: t.employee.id,
          firstName: t.employee.first_name,
          lastName: t.employee.last_name,
        }
      : undefined,
    status: 'confirmed' as const,
    notes: t.notes || '',
    coinChange: t.coin_change,
    bankAccountAmountChange: t.bank_account_amount_change,
    fullReversal: t.full_reversal || false,
  }));
}

/**
 * Transforms linked transactions to the legacy format
 */
function transformLinkedTransactions(
  links?: CashListTransactionGroupSingle['cash_list_transaction_group_links']
): TransformedCashTransactionGroup['transactions'] {
  if (!links) {
    return [];
  }

  return links
    .filter((link) => link.transaction)
    .map((link) => {
      const transaction = link.transaction!;
      const creator = transaction.creator_employee;

      return {
        id: transaction.id,
        initiator: creator
          ? {
              id: creator.id,
              firstName: creator.first_name,
              lastName: creator.last_name,
              fullName: `${creator.first_name || ''} ${
                creator.last_name || ''
              }`.trim(),
            }
          : undefined,
        amount: transaction.amount,
        additionalFeeAmount: transaction.additional_fee_amount,
        includedFeeAmount: transaction.included_fee_amount,
        additionalFeesDivertedToPflegenavi:
          transaction.additional_fees_diverted_to_pflegenavi,
        type: transaction.type as TransactionType,
        sourceType: transaction.source_type as TransactionSourceType,
        receiptType: undefined, // TODO: Get this from receipt through transaction
        resident: transaction.resident
          ? {
              id: transaction.resident.id,
              firstName: transaction.resident.first_name,
              lastName: transaction.resident.last_name,
            }
          : undefined,
        notes: transaction.notes,
        date: new Date(transaction.creation_date),
        initiatedBy: transaction.initiated_by as TransactionInitiatedBy,
        additionalData: {},
      };
    });
}
